import React, { useMemo, useState } from 'react';
import { Sparkles, Loader2, FileText, X, CheckCircle2, AlertCircle, FileCheck2, Info, ShieldAlert } from 'lucide-react';
import { fmt$, isIncomplete, isDeclining, missingLabels, refusalText, CAT_META, NAVY_GRADIENT } from './constants';
import { BRAND } from '../kit/tokens';
import SsnReviewDialog from '../SsnReviewDialog';

// ============================================================================
// AnalysisResults — richer breakdown after multi-file drop
// ============================================================================
export function AnalysisResults({ analysis, onDismiss, onRequestDocs, requestingDocs, onSendToBorrower, onReviewed }) {
  const filesByStatus = useMemo(() => ({
    income: analysis.files.filter(f => f.is_income_document && !f.error),
    skipped: analysis.files.filter(f => !f.is_income_document),
    errors: analysis.files.filter(f => f.error),
  }), [analysis]);

  const agg = analysis.aggregate_pay;
  const byCat = Object.entries(agg?.by_category_ytd || {}).sort((a, b) => b[1] - a[1]);
  const baseCategories = byCat.filter(([k]) => CAT_META[k]?.base);
  const varCategories = byCat.filter(([k]) => CAT_META[k] && !CAT_META[k].base);

  return (
    <div className="space-y-4" data-testid="analysis-results">
      {/* File classification chips */}
      <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand-accent">
              <FileCheck2 size={13} /> Documents analyzed
            </div>
            <div className="font-serif text-lg font-semibold mt-0.5 text-brand-navy">
              {filesByStatus.income.length} income doc{filesByStatus.income.length !== 1 ? 's' : ''}
              {filesByStatus.skipped.length > 0 && <span className="text-slate-400 font-normal"> · {filesByStatus.skipped.length} skipped</span>}
              {filesByStatus.errors.length > 0 && <span className="text-red-500 font-normal"> · {filesByStatus.errors.length} error{filesByStatus.errors.length !== 1 ? 's' : ''}</span>}
            </div>
          </div>
          <button onClick={onDismiss} className="p-2 hover:bg-slate-100 rounded-lg text-slate-500" data-testid="analysis-dismiss"><X size={14} /></button>
        </div>
        <div className="space-y-2">
          {analysis.files.map((f, i) => <FileRow key={i} file={f} onReviewed={onReviewed} />)}
        </div>
        {(analysis.borrower_name || analysis.employer) && (
          <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap gap-x-6 gap-y-1 text-[13px]">
            {analysis.borrower_name && <div><span className="text-slate-500">Borrower:</span> <b className="text-brand-navy">{analysis.borrower_name}</b></div>}
            {analysis.employer && <div><span className="text-slate-500">Employer:</span> <b className="text-brand-navy">{analysis.employer}</b></div>}
          </div>
        )}
      </div>

      {/* Aggregate pay summary */}
      {agg && (agg.total_ytd > 0 || agg.total_period > 0) && (
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-widest font-bold text-brand-accent">Aggregate Pay (YTD)</div>
              <div className="font-serif text-lg font-semibold text-brand-navy">Full earnings breakdown</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Total YTD</div>
              <div className="font-serif font-bold text-2xl text-brand-navy">{fmt$(agg.total_ytd)}</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* BASE column */}
            <div className="rounded-xl p-4 border" style={{ background: 'rgba(107,168,46,0.06)', borderColor: 'rgba(107,168,46,0.3)' }}>
              <div className="flex items-baseline justify-between mb-2">
                <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: '#3a5717' }}>Base Income</div>
                <div className="font-serif font-bold text-xl" style={{ color: '#3a5717' }}>{fmt$(agg.base_ytd)}</div>
              </div>
              <div className="text-[10px] text-slate-500 mb-2">Includes PTO, Holiday, Vacation, Sick — Fannie B3-3.1-01</div>
              <div className="space-y-1">
                {baseCategories.length === 0 && <div className="text-[11px] text-slate-400 italic">Detected in aggregate but not itemized.</div>}
                {baseCategories.map(([k, v]) => (
                  <CategoryRow key={k} category={k} amount={v} />
                ))}
              </div>
            </div>

            {/* VARIABLE column */}
            <div className="rounded-xl p-4 border" style={{ background: agg.variable_ytd > 0 ? 'rgba(245,158,11,0.06)' : '#f8fafc', borderColor: agg.variable_ytd > 0 ? 'rgba(245,158,11,0.3)' : '#e2e8f0' }}>
              <div className="flex items-baseline justify-between mb-2">
                <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: agg.variable_ytd > 0 ? '#92400e' : '#64748b' }}>Variable Income</div>
                <div className="font-serif font-bold text-xl" style={{ color: agg.variable_ytd > 0 ? '#92400e' : '#64748b' }}>{fmt$(agg.variable_ytd)}</div>
              </div>
              <div className="text-[10px] text-slate-500 mb-2">Overtime · Bonus · Commission · Tips · etc — requires 24-mo history</div>
              <div className="space-y-1">
                {varCategories.length === 0 && <div className="text-[11px] text-slate-400 italic">No variable pay detected.</div>}
                {varCategories.map(([k, v]) => (
                  <CategoryRow key={k} category={k} amount={v} />
                ))}
              </div>
            </div>
          </div>

          {/* PTO/Holiday info banner */}
          <div className="mt-4 flex items-start gap-2 p-3 rounded-lg" style={{ background: 'rgba(107,168,46,0.06)', color: '#3a5717' }}>
            <Info size={13} className="mt-0.5 shrink-0" />
            <div className="text-[11px] leading-relaxed">
              <b>PTO, holiday, vacation, sick, bereavement, and jury-duty pay are counted as BASE income</b> under Fannie B3-3.1-01 — they represent guaranteed employer time-off compensation and do not require the 24-month variable-income history.
            </div>
          </div>
        </div>
      )}

      {/* Variable income warning + docs request */}
      {analysis.variable_income?.detected && (
        <div className="rounded-2xl p-5 border-2" style={{ borderColor: '#f59e0b', background: '#FFF7ED' }}>
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 font-bold" style={{ background: '#f59e0b', color: '#fff' }}>!</div>
            <div className="flex-1">
              <div className="font-semibold text-sm" style={{ color: '#92400e' }}>Variable Income Detected: {analysis.variable_income.types.join(', ')}</div>
              <div className="text-xs text-slate-700 mt-1">{analysis.variable_income.checklist_message}</div>
              <ul className="mt-2 text-xs space-y-0.5">
                {(analysis.variable_income.docs_needed || []).map((d, i) => (
                  <li key={i} className="flex items-start gap-1.5"><span style={{ color: '#f59e0b' }}>•</span><span className="text-slate-700">{d}</span></li>
                ))}
              </ul>
              <div className="mt-3 flex flex-wrap gap-2 items-center">
                <button onClick={onRequestDocs} disabled={requestingDocs} data-testid="request-variable-docs"
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white flex items-center gap-1.5" style={{ background: '#f59e0b' }}>
                  {requestingDocs ? <><Loader2 size={12} className="animate-spin" /> Creating task...</> : 'Add to Tasks'}
                </button>
                <button onClick={onSendToBorrower} data-testid="send-to-borrower"
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white flex items-center gap-1.5" style={{ background: NAVY_GRADIENT }}>
                  <Sparkles size={12} /> Send to Borrower
                </button>
                <div className="text-[10px] text-slate-500">{analysis.variable_income.guideline}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Liabilities */}
      {analysis.liabilities?.detected && (
        <div className="rounded-2xl p-5 border-2" style={{ borderColor: '#ef4444', background: '#FEF2F2' }}>
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 font-bold" style={{ background: '#ef4444', color: '#fff' }}>$</div>
            <div className="flex-1">
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-sm" style={{ color: '#7f1d1d' }}>Payroll Deductions Detected — Impacts DTI</div>
                <div className="font-serif text-lg font-bold" style={{ color: '#ef4444' }}>{fmt$(analysis.liabilities.total_monthly)}/mo</div>
              </div>
              <div className="text-xs text-slate-700 mt-1">{analysis.liabilities.note}</div>
              <div className="mt-2 space-y-1.5">
                {analysis.liabilities.items.map((l, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-white/70 p-2 rounded-lg">
                    <div>
                      <div className="font-semibold" style={{ color: '#7f1d1d' }}>{l.label}</div>
                      <div className="text-[11px] text-slate-500">{l.note} {l.source_file && `· ${l.source_file}`}</div>
                    </div>
                    <div className="text-right">
                      {l.monthly_amount != null ? <div className="font-serif font-bold" style={{ color: '#ef4444' }}>{fmt$(l.monthly_amount)}/mo</div> : <div className="text-slate-500 italic">TBD</div>}
                      {l.per_period_amount && <div className="text-[10px] text-slate-400">{fmt$(l.per_period_amount)}/period</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function FileRow({ file, onReviewed }) {
  const [reviewing, setReviewing] = useState(false);
  const isErr = !!file.error;
  const isHeld = !!file.needs_masking;
  const isReviewed = isHeld && !!file.ssn_reviewed; // reviewed; read with the drop's other held files
  const isSkip = !file.is_income_document;
  const iconColor = isErr ? '#ef4444' : isHeld ? '#b45309' : isSkip ? '#94a3b8' : BRAND.accent;
  return (
    <div className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-100" data-testid={`file-row-${file.filename}`}>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${iconColor}20` }}>
        {isErr ? <AlertCircle size={14} style={{ color: iconColor }} /> : isHeld ? <ShieldAlert size={14} style={{ color: iconColor }} /> : isSkip ? <FileText size={14} style={{ color: iconColor }} /> : <CheckCircle2 size={14} style={{ color: iconColor }} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold truncate text-brand-navy">{file.filename}</div>
        <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
          <span className="font-semibold uppercase tracking-wider text-[10px] px-1.5 py-0.5 rounded" style={{ background: `${iconColor}18`, color: iconColor }}>{file.classified_label}</span>
          {file.pay_summary?.total_ytd > 0 && <span>YTD {fmt$(file.pay_summary.total_ytd)}</span>}
          {file.pay_summary?.variable_ytd > 0 && <span className="text-amber-700">Variable {fmt$(file.pay_summary.variable_ytd)}</span>}
          {file.error && <span className="text-red-600">· {file.error}</span>}
          {file.skipped_reason && <span className="italic">· {file.skipped_reason}</span>}
        </div>
      </div>
      {isReviewed && (
        <div className="shrink-0" data-testid={`file-reviewed-${file.filename}`}>
          <span className="inline-block text-[11px] font-semibold px-2 py-1 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-200">SSN masked · review the other files</span>
        </div>
      )}
      {isHeld && !isReviewed && (
        <div className="shrink-0 flex items-center gap-2" data-testid={`file-held-${file.filename}`}>
          <span className="inline-block text-[11px] font-semibold px-2 py-1 rounded-md bg-amber-50 text-amber-800 border border-amber-200">Needs SSN masking</span>
          <button type="button" onClick={() => setReviewing(true)}
            className="text-[11px] font-semibold px-2.5 py-1 rounded-md bg-brand-navy text-white"
            data-testid={`file-review-${file.filename}`}>Review</button>
          <SsnReviewDialog reviewId={file.review_id} filename={file.filename} open={reviewing} onOpenChange={setReviewing} onReviewed={onReviewed} />
        </div>
      )}
      {file.paired_years?.role === 'y1' && (
        <div className="text-right shrink-0 max-w-[45%]" data-testid={`file-paired-y1-${file.filename}`}>
          <span className="inline-block text-[11px] font-semibold px-2 py-1 rounded-md bg-slate-100 text-slate-600 border border-slate-200">
            Used as Y1 for {file.paired_years.other_filename}
          </span>
        </div>
      )}
      {file.superseded_by && (
        <div className="text-right shrink-0 max-w-[45%]" data-testid={`file-superseded-${file.filename}`}>
          <span className="inline-block text-[11px] font-semibold px-2 py-1 rounded-md bg-slate-100 text-slate-600 border border-slate-200">
            Not used — covered by {file.superseded_by}
          </span>
        </div>
      )}
      {file.paired_business_return?.role === 'business_return' && (
        <div className="text-right shrink-0 max-w-[45%]" data-testid={`file-paired-business-return-${file.filename}`}>
          <span className="inline-block text-[11px] font-semibold px-2 py-1 rounded-md bg-slate-100 text-slate-600 border border-slate-200">
            Add-backs used with {file.paired_business_return.other_filename}
          </span>
        </div>
      )}
      {file.paired_years?.role !== 'y1' && !file.superseded_by && file.paired_business_return?.role !== 'business_return' && isIncomplete(file.calculation) && (
        <div className="text-right shrink-0 max-w-[45%]" data-testid={`file-missing-${file.filename}`}>
          <span className="inline-block text-[11px] font-semibold px-2 py-1 rounded-md bg-amber-50 text-amber-800 border border-amber-200">
            {(file.calculation.missing_fields || []).some(isDeclining) ? refusalText(file.calculation) : `Missing: ${missingLabels(file.calculation)}`}
          </span>
        </div>
      )}
      {file.calculation?.qualifying_monthly > 0 && (
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Qual/mo</div>
          <div className="font-serif font-bold text-[15px] text-brand-accent">{fmt$(file.calculation.qualifying_monthly)}</div>
          {file.paired_years?.role === 'y2' && (
            <div className="text-[10px] text-slate-500" data-testid={`file-paired-y2-${file.filename}`}>
              Y1 from {file.paired_years.other_filename} ({file.paired_years.other_tax_year})
            </div>
          )}
          {file.paired_business_return?.role === 'k1' && (
            <div className="text-[10px] text-slate-500" data-testid={`file-paired-k1-${file.filename}`}>
              Add-backs from {file.paired_business_return.other_filename} × {file.paired_business_return.ownership_pct}%
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function CategoryRow({ category, amount }) {
  const meta = CAT_META[category] || { label: category, base: false };
  const Icon = meta.icon;
  return (
    <div className="flex items-center justify-between text-[12px]">
      <div className="flex items-center gap-1.5">
        {Icon && <Icon size={11} className="opacity-60" />}
        <span className="text-slate-700">{meta.label}</span>
      </div>
      <span className="font-semibold text-brand-navy">{fmt$(amount)}</span>
    </div>
  );
}
